
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface ProductCardProps {
  product: {
    id: string;
    category: string;
    name: string;
    description: string;
    image: string;
    features: string[];
    applications: string[];
    icon: string;
  };
  index: number;
  inView: boolean;
  isActive: boolean;
  onHover: () => void;
}

const ProductCard = ({
  product,
  index,
  inView,
  isActive,
  onHover,
}: ProductCardProps) => {
  return (
    <motion.div
      id={product.id}
      initial={{ opacity: 0, y: 80, scale: 0.95 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{
        duration: 0.7,
        delay: index * 0.15,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      onMouseEnter={onHover}
      className="group relative"
    >
      <div
        className={`relative bg-white rounded-2xl lg:rounded-3xl overflow-hidden border transition-all duration-700 ${
          isActive
            ? 'border-green/30 shadow-2xl shadow-green/10'
            : 'border-gray-100 shadow-lg hover:shadow-2xl'
        }`}
      >
        {/* Top Accent Line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 + index * 0.15 }}
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green via-teal to-green origin-left"
        />

        {/* Image Section */}
        <div className="relative w-full h-[280px] sm:h-[320px] lg:h-[360px] bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 120, repeat: Infinity, ease: 'linear' }}
              className="absolute -top-20 -right-20 w-60 h-60 border border-navy rounded-full"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
              className="absolute -bottom-10 -left-10 w-40 h-40 border border-teal rounded-full"
            />
          </div>

          {/* Product Image */}
          <motion.div
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="relative w-full h-full flex items-center justify-center p-8 sm:p-10"
          >
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain drop-shadow-lg group-hover:drop-shadow-2xl transition-all duration-700"
            />
          </motion.div>

          {/* Category Badge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.5 + index * 0.15 }}
            className="absolute top-4 right-4"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow-md border border-gray-100">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${product.icon} text-teal text-sm`} />
              </div>
              <span className="text-navy text-xs font-semibold tracking-wider">
                {product.category}
              </span>
            </div>
          </motion.div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-navy/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>

        {/* Content Section */}
        <div className="p-6 sm:p-8 lg:p-10">
          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-3 sm:mb-4 group-hover:text-teal transition-colors duration-500">
            {product.name}
          </h2>

          {/* Description */}
          <p className="text-gray-600 text-sm sm:text-[15px] leading-relaxed mb-6">
            {product.description}
          </p>

          {/* Features */}
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {product.features.map((feature, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{
                    duration: 0.4,
                    delay: 0.6 + idx * 0.08 + index * 0.1,
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal/8 text-teal text-xs font-medium rounded-full border border-teal/15"
                >
                  <i className="ri-check-line text-xs" />
                  {feature}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Applications */}
          <div className="mb-8">
            <h4 className="text-xs font-semibold text-navy/60 uppercase tracking-wider mb-3">
              Kullanım Alanları
            </h4>
            <div className="flex flex-wrap gap-2">
              {product.applications.map((app, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-gray-50 text-gray-600 text-xs rounded-md border border-gray-100"
                >
                  {app}
                </span>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Link
              to="/quote"
              className="flex-1 text-center px-6 py-3 bg-gradient-to-r from-green to-teal text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-green/20 hover:-translate-y-0.5 transition-all duration-400 cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center justify-center gap-2">
                Teklif Al
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
            <Link
              to="/contact"
              className="flex-1 text-center px-6 py-3 bg-transparent border-2 border-navy/15 text-navy text-sm font-semibold rounded-lg hover:bg-navy hover:text-white hover:border-navy transition-all duration-400 cursor-pointer whitespace-nowrap"
            >
              Detaylı Bilgi
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
